import {
  formatCurrency,
  formatDateTimeShortToBrazil,
  formatDateToBrazil,
} from '@/shared/utils';
import { Dialog, Tag, Text } from '@noorden-insurtech/alba-design-system';
import { Loader2 } from 'lucide-react';
import type { PolicyChargeAttempt } from '../../../services/policy-charges';
import { usePolicyChargeAttempts } from '../hooks';

type AttemptTone = 'lightGreen' | 'error' | 'gray' | 'yellow';

/**
 * Como cada desfecho é lido por quem está olhando a tela. Falha continua
 * visível — esconder as tentativas que não deram certo anularia o motivo de o
 * histórico existir.
 */
const outcomeConfig: Record<string, { label: string; tone: AttemptTone }> = {
  CREATED: { label: 'Link gerado', tone: 'lightGreen' },
  ADOPTED: { label: 'Link recuperado', tone: 'lightGreen' },
  SUPERSEDED: { label: 'Substituída', tone: 'gray' },
  IN_FLIGHT: { label: 'Em andamento', tone: 'yellow' },
  THROTTLED: { label: 'Limite de taxa', tone: 'yellow' },
  PROVIDER_ERROR: { label: 'Falha no provedor', tone: 'error' },
  INVALID: { label: 'Dados inválidos', tone: 'error' },
  UNKNOWN: { label: 'Desfecho desconhecido', tone: 'error' },
};

const createdViaLabel: Record<string, string> = {
  WORKFLOW: 'Emissão automática',
  SCHEDULER: 'Agendamento automático',
  RECONCILIATION: 'Reprocessamento automático',
  MANUAL: 'Ação manual',
};

/** O que aconteceu com o e-mail ao segurado deste link. */
function describeEmail(attempt: PolicyChargeAttempt): string | undefined {
  const email = attempt.notifications?.find(item => item.channel === 'EMAIL');
  if (!email) return undefined;

  switch (email.status) {
    case 'SENT':
      return `enviado em ${formatDateTimeShortToBrazil(email.sentAt)}${
        email.recipient ? ` para ${email.recipient}` : ''
      }${email.tries > 1 ? ` (${email.tries} envios)` : ''}`;
    case 'FAILED':
      return `falhou${email.tries > 1 ? ` após ${email.tries} envios` : ''}${
        email.error ? ` — ${email.error}` : ''
      }`;
    case 'SKIPPED':
      return email.error ?? 'não enviado';
    default:
      return undefined;
  }
}

interface ChargeAttemptsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  policyId: string;
  installmentNumber?: number;
}

export function ChargeAttemptsDialog({
  isOpen,
  onClose,
  policyId,
  installmentNumber,
}: ChargeAttemptsDialogProps) {
  const { data: attempts = [], isLoading } = usePolicyChargeAttempts(
    policyId,
    installmentNumber,
    isOpen
  );

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={details => !details.open && onClose()}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-full max-w-3xl'>
          <Dialog.Header>
            <Dialog.Title>
              {installmentNumber == null
                ? 'Tentativas de cobrança da apólice'
                : installmentNumber === 0
                  ? 'Tentativas de cobrança — todas as parcelas'
                  : `Tentativas de cobrança — parcela ${installmentNumber}`}
            </Dialog.Title>
            <Dialog.CloseTrigger asChild>
              <Dialog.CloseButton />
            </Dialog.CloseTrigger>
          </Dialog.Header>

          <Dialog.Body>
            {isLoading ? (
              <div className='flex items-center justify-center py-10'>
                <Loader2 className='h-6 w-6 animate-spin text-alba-gray-400' />
              </div>
            ) : attempts.length === 0 ? (
              <Text className='py-6 text-center text-sm text-alba-gray-500'>
                Nenhuma tentativa registrada.
              </Text>
            ) : (
              <div className='flex flex-col gap-3'>
                {attempts.map(attempt => (
                  <AttemptRow
                    key={`${attempt.installmentNumber}-${attempt.attempt}`}
                    attempt={attempt}
                    showInstallment={installmentNumber == null}
                  />
                ))}
              </div>
            )}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

function AttemptRow({
  attempt,
  showInstallment,
}: {
  attempt: PolicyChargeAttempt;
  showInstallment: boolean;
}) {
  const config = outcomeConfig[attempt.outcome] ?? {
    label: attempt.outcome,
    tone: 'gray' as AttemptTone,
  };
  // No PIX Automático o sucesso cria o MANDATO, não um link: "Link gerado"
  // faria o corretor procurar um link de pagamento que não foi enviado. As
  // falhas mantêm a etiqueta — é ela que diz o que deu errado.
  const isMandateSuccess =
    attempt.billingFlow === 'RECURRENCE' &&
    (attempt.outcome === 'CREATED' || attempt.outcome === 'ADOPTED');

  return (
    <div className='rounded-lg border border-alba-gray-200 p-3'>
      <div className='flex flex-wrap items-center gap-2'>
        {!isMandateSuccess && (
          <Tag.Root variant='solid' colorScheme={config.tone} size='sm'>
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        )}
        <Text className='text-sm font-medium text-alba-black-500'>
          {showInstallment
            ? attempt.installmentNumber === 0
              ? 'Todas as parcelas · '
              : `Parcela ${attempt.installmentNumber} · `
            : ''}
          Tentativa {attempt.attempt}
        </Text>
        <Text className='text-xs text-alba-gray-500'>
          {formatDateToBrazil(attempt.requestedAt)}
        </Text>
      </div>

      <div className='mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-alba-gray-600 sm:grid-cols-2'>
        <span>
          Valor cobrado: <strong>{formatCurrency(attempt.amount ?? 0)}</strong>
        </span>
        <span>Vencimento: {formatDateToBrazil(attempt.dueDate)}</span>
        <span>
          Origem: {createdViaLabel[attempt.createdVia] ?? attempt.createdVia}
        </span>
        {attempt.createdByUserName && (
          <span>Solicitado por: {attempt.createdByUserName}</span>
        )}
        {attempt.orderStatus && (
          <span>Status do pedido: {attempt.orderStatus}</span>
        )}
        {attempt.httpStatus != null && <span>HTTP: {attempt.httpStatus}</span>}
        {describeEmail(attempt) && (
          <span>E-mail ao segurado: {describeEmail(attempt)}</span>
        )}
      </div>

      {attempt.manualAmountReason && (
        <Text className='mt-2 text-xs text-alba-gray-600'>
          <strong>Justificativa:</strong> {attempt.manualAmountReason}
        </Text>
      )}

      {attempt.errorMessage && (
        <Text className='mt-2 break-words text-xs text-red-500'>
          {attempt.errorMessage}
        </Text>
      )}
    </div>
  );
}
